import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Info, Layers, ShieldCheck, AlertTriangle } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import PreContractService from '../../services/preContractService';

const ViewPolicy = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { error: showError, toasts, removeToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const response = await PreContractService.getPolicy(id);
        if (response?.data) {
          setPolicy(response.data);
        } else {
          showError('Policy not found.');
          navigate('/contracts/pre-contract/policies');
        }
      } catch (err) {
        showError('Failed to load policy details.');
        navigate('/contracts/pre-contract/policies');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPolicy();
  }, [id, navigate]);

  const headerActions = [
    {
      label: 'Back to Policies',
      icon: <ArrowLeft size={18} />,
      onClick: () => navigate('/contracts/pre-contract/policies'),
      variant: 'outline',
    },
  ];

  if (isLoading || !policy) {
    return (
      <div className="p-5 text-center">
        Loading policy details...
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="View Policy"
        description="Read-only view of pre-contract compliance policy."
        actions={headerActions}
      />

      <div className="page-container">
        <div className="row">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body p-4">
                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label fw-bold">Policy Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={policy.name || ''}
                      readOnly
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label fw-bold">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={policy.description || ''}
                      readOnly
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Rule Type</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        <Layers size={16} />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={policy.type || 'Value Threshold'}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-bold">Enforcement Level</label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        {policy.enforcement === 'Critical Block' ? (
                          <ShieldCheck size={16} className="text-danger" />
                        ) : (
                          <AlertTriangle size={16} className="text-warning" />
                        )}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={policy.enforcement || 'Critical Block'}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label fw-bold">Threshold / Condition</label>
                    <input
                      type="text"
                      className="form-control"
                      value={policy.thresholdValue || ''}
                      readOnly
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-bold">Status</label>
                    <div className="form-control bg-light d-flex align-items-center">
                      <span
                        className={`badge ${
                          policy.isActive ? 'bg-success' : 'bg-secondary'
                        }`}
                      >
                        {policy.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card bg-light border-0 shadow-sm mb-4">
              <div className="card-body">
                <h6 className="fw-bold d-flex align-items-center gap-2 mb-3">
                  <Info size={18} className="text-primary" />
                  Policy Details
                </h6>
                <p className="small text-secondary mb-1">
                  This is a read-only view. To modify this policy, return to the list and
                  choose the Edit action.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ViewPolicy;

